(() => {
  const asNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : null;
  const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };
  const validPlaylist = (value) => {
    if (!value || !Array.isArray(value.parts) || value.parts.length === 0) return null;
    const parts = value.parts.map((part) => {
      const start = asNumber(part.start ?? part.source_nominal_start_s);
      const end = asNumber(part.source_nominal_end_s);
      const duration = asNumber(part.duration ?? part.video_duration_s) ?? (start !== null && end !== null ? end - start : null);
      return { url: String(part.url || ''), start, duration };
    });
    if (parts.some((part) => !part.url || part.start === null || part.start < 0 || part.duration === null || part.duration <= 0)) return null;
    parts.sort((a, b) => a.start - b.start);
    if (parts[0].start !== 0) return null;
    for (let index = 1; index < parts.length; index += 1) {
      if (Math.abs(parts[index].start - (parts[index - 1].start + parts[index - 1].duration)) > 0.05) return null;
    }
    return parts;
  };
  const validNarrationCues = (value, totalDuration) => {
    if (!Array.isArray(value) || value.length !== 29) return null;
    const cues = value.map((cue) => ({ start: asNumber(cue.start), end: asNumber(cue.end), text: String(cue.text || '').trim() }));
    if (cues.some((cue) => cue.start === null || cue.end === null || cue.end <= cue.start || !cue.text || cue.end > totalDuration + 0.001)) return null;
    cues.sort((left, right) => left.start - right.start);
    for (let index = 1; index < cues.length; index += 1) if (cues[index].start < cues[index - 1].end) return null;
    return cues;
  };
  const init = (root) => {
    const video = root.querySelector('video');
    const status = root.querySelector('[data-kxcd-player-status]');
    const controls = root.querySelector('[data-kxcd-global-controls]');
    const scrubber = root.querySelector('[data-kxcd-global-scrubber]');
    const timeLabel = root.querySelector('[data-kxcd-global-time]');
    const partLabel = root.querySelector('[data-kxcd-part-label]');
    const fallback = root.querySelector('[data-kxcd-fallback]');
    const logo = root.querySelector('[data-kxcd-logo]');
    const wordmark = root.querySelector('[data-kxcd-wordmark]');
    const captionToggle = root.querySelector('[data-kxcd-caption-toggle]');
    const setStatus = (message) => { if (status) status.textContent = message; };
    const tabs = root.querySelectorAll('[data-kxcd-tab]');
    const panels = root.querySelectorAll('[data-kxcd-panel]');
    let parts = null;
    let activePart = 0;
    let sourceChange = false;
    let partReady = false;
    let loadRequest = 0;
    let narrationCues = [];
    let captionTrack = null;
    let captionCueRefs = [];
    let captionsEnabled = false;
    let syncingCaptionMode = false;
    const hasFallback = Boolean(video?.querySelector('source')?.src);

    const totalDuration = () => parts ? parts[parts.length - 1].start + parts[parts.length - 1].duration : (video?.duration || 0);
    const globalTime = () => parts && video ? parts[activePart].start + (video.currentTime || 0) : (video?.currentTime || 0);
    const updateTimeline = () => {
      if (!video) return;
      const current = globalTime();
      if (scrubber) { scrubber.value = String(Math.min(totalDuration(), Math.max(0, current))); }
      if (timeLabel) timeLabel.textContent = `${formatTime(current)} / ${formatTime(totalDuration())}`;
      if (partLabel && parts) partLabel.textContent = `Part ${activePart + 1} of ${parts.length}`;
    };
    const playOrReport = () => video.play().catch(() => setStatus('Playback is blocked by this browser. Use the video play control to continue.'));
    const findPart = (time) => {
      const bounded = Math.max(0, Math.min(totalDuration(), time));
      const index = parts.findIndex((part, candidate) => bounded < part.start + part.duration || candidate === parts.length - 1);
      return { index, localTime: Math.max(0, bounded - parts[index].start) };
    };
    const updateCaptionToggle = () => {
      if (!captionToggle) return;
      captionToggle.hidden = !captionTrack;
      captionToggle.setAttribute('aria-pressed', String(captionsEnabled));
      captionToggle.textContent = captionsEnabled ? 'Hide coaching captions' : 'Show coaching captions';
    };
    const clearCaptionCues = () => {
      if (!captionTrack) return;
      captionCueRefs.forEach((cue) => captionTrack.removeCue(cue));
      captionCueRefs = [];
    };
    const applyCaptionMode = () => {
      if (!captionTrack) return;
      syncingCaptionMode = true;
      captionTrack.mode = captionsEnabled ? 'showing' : 'disabled';
      syncingCaptionMode = false;
    };
    const syncCaptionCues = (partIndex) => {
      clearCaptionCues();
      if (!captionTrack || !parts?.[partIndex]) return;
      const part = parts[partIndex];
      narrationCues.filter((cue) => cue.start >= part.start - 0.001 && cue.end <= part.start + part.duration + 0.001).forEach((cue) => {
        const nativeCue = new VTTCue(Math.max(0, cue.start - part.start), Math.min(part.duration, cue.end - part.start), cue.text);
        captionTrack.addCue(nativeCue);
        captionCueRefs.push(nativeCue);
      });
      applyCaptionMode();
    };
    const setCaptionsEnabled = (enabled) => {
      captionsEnabled = Boolean(enabled) && Boolean(captionTrack);
      applyCaptionMode();
      updateCaptionToggle();
    };
    const setupCaptions = (cues) => {
      narrationCues = cues || [];
      if (!video || !narrationCues.length || typeof video.addTextTrack !== 'function' || typeof VTTCue !== 'function') return updateCaptionToggle();
      video.textTracks.addEventListener('change', () => {
        if (!captionTrack || syncingCaptionMode) return;
        captionsEnabled = captionTrack.mode === 'showing';
        updateCaptionToggle();
      });
    };
    const installCaptionTrack = () => {
      if (!video || !narrationCues.length || typeof video.addTextTrack !== 'function' || typeof VTTCue !== 'function') return updateCaptionToggle();
      if (!captionTrack) {
        captionTrack = video.addTextTrack('captions', 'Coaching narration', 'en');
        applyCaptionMode();
      }
      syncCaptionCues(activePart);
      updateCaptionToggle();
    };
    const loadPart = (index, localTime, shouldPlay) => {
      if (!parts || !video || index < 0 || index >= parts.length) return;
      const request = ++loadRequest;
      const previousVolume = video.volume;
      const previousRate = video.playbackRate;
      activePart = index;
      partReady = false;
      sourceChange = true;
      clearCaptionCues();
      updateCaptionToggle();
      video.src = parts[index].url;
      video.load();
      video.addEventListener('loadedmetadata', () => {
        if (request !== loadRequest) return;
        if (localTime > 0) video.currentTime = Math.min(localTime, Math.max(0, video.duration - 0.01));
        video.volume = previousVolume;
        video.playbackRate = previousRate;
        partReady = true;
        sourceChange = false;
        installCaptionTrack();
        updateTimeline();
        if (shouldPlay) playOrReport();
      }, { once: true });
      video.addEventListener('error', () => {
        if (request !== loadRequest) return;
        sourceChange = false;
        setStatus('This video part could not load. Try the full review link below.');
        if (fallback) fallback.hidden = false;
      }, { once: true });
      updateTimeline();
    };
    const seekGlobal = (time, shouldPlay) => {
      if (!video) return;
      if (!parts) {
        video.currentTime = time;
        if (shouldPlay) playOrReport();
        return;
      }
      const target = findPart(time);
      if (target.index === activePart && partReady) {
        video.currentTime = target.localTime;
        updateTimeline();
        if (shouldPlay) playOrReport();
        return;
      }
      loadPart(target.index, target.localTime, shouldPlay);
    };
    const revealPlayer = () => {
      const player = root.querySelector('.kxcd-video-wrap');
      if (!player) return;
      player.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    };

    const activateTab = (tabName) => {
      tabs.forEach((item) => { const active = item.dataset.kxcdTab === tabName; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); });
      panels.forEach((panel) => { panel.hidden = panel.dataset.kxcdPanel !== tabName; });
    };
    root.querySelectorAll('[data-kxcd-time]').forEach((button) => button.addEventListener('click', () => {
      if (button.hasAttribute('data-kxcd-go-recipient')) activateTab('recipient');
      seekGlobal(Number(button.dataset.kxcdTime), true);
      revealPlayer();
    }));
    if (scrubber) scrubber.addEventListener('input', () => seekGlobal(Number(scrubber.value), false));
    if (captionToggle) captionToggle.addEventListener('click', () => setCaptionsEnabled(!captionsEnabled));
    if (video) {
      video.addEventListener('timeupdate', updateTimeline);
      video.addEventListener('play', () => setStatus(parts ? `Playing review · Part ${activePart + 1} of ${parts.length}` : 'Playing full review.'));
      video.addEventListener('pause', () => { if (!video.ended) setStatus(parts ? `Paused · Part ${activePart + 1} of ${parts.length}` : 'Paused.'); });
      video.addEventListener('ended', () => {
        if (parts && activePart < parts.length - 1) loadPart(activePart + 1, 0, true);
        else setStatus('Review ended.');
      });
      video.addEventListener('error', () => { if (!sourceChange) setStatus('Video unavailable. Try the full review link below.'); });
    }
    tabs.forEach((tab) => tab.addEventListener('click', () => activateTab(tab.dataset.kxcdTab)));
    const copy = root.querySelector('[data-kxcd-copy]');
    if (copy) copy.addEventListener('click', async () => {
      const script = root.querySelector('.kxcd-script');
      const paragraphs = script ? [...script.querySelectorAll('p')].map((paragraph) => paragraph.textContent.trim()).filter(Boolean).join('\n\n') : '';
      if (!paragraphs || !navigator.clipboard) return;
      try { await navigator.clipboard.writeText(paragraphs); copy.textContent = 'Copied'; } catch { copy.textContent = 'Copy unavailable'; }
    });

    const playlistUrl = root.dataset.kxcdPlaylistUrl;
    if (playlistUrl && video) {
      setStatus('Loading the review…');
      fetch(playlistUrl, { credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => {
          if (data && typeof data.poster_url === 'string' && data.poster_url) video.poster = data.poster_url;
          if (data && typeof data.logo_url === 'string' && data.logo_url && logo) {
            logo.src = data.logo_url;
            logo.hidden = false;
            if (wordmark) wordmark.hidden = true;
          }
          const playlist = validPlaylist(data);
          if (!playlist) { setStatus(hasFallback ? 'Ready to play the full review.' : 'Add a hosted full-review URL or a valid playlist before sharing.'); return; }
          parts = playlist;
          setupCaptions(validNarrationCues(data?.narration_cues, totalDuration()));
          if (controls) controls.hidden = false;
          if (scrubber) { scrubber.max = String(totalDuration()); scrubber.value = '0'; }
          loadPart(0, 0, false);
          setStatus(`Review ready · Part 1 of ${parts.length}.`);
        })
        .catch(() => setStatus(hasFallback ? 'Ready to play the full review.' : 'Add a hosted full-review URL or a valid playlist before sharing.'));
    }
  };
  document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-kxce-coaching-demo]').forEach(init));
})();
